import { useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { InfoWindow } from '@vis.gl/react-google-maps';

import HomeEditModal from '@rover/components/src/formFields/LocationInput/components/HomeEditModal';
import useHomeAddress from '@rover/components/src/formFields/LocationInput/hooks/useHomeAddress';
import { SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import { Image } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import Marker from '@rover/react-lib/src/components/GeoMap/Marker/Marker';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import homeMarkerImage from './home-marker.png';
import HomeAddressCard from './HomeAddressCard';

type Props = {
  onEdit?: (newAddressData: UserInformationAddress) => void;
};

const HomeAddressMarker = ({ onEdit }: Props): JSX.Element | null => {
  const { t } = useLingui();
  const { colors } = useTheme();

  const { address, formattedAddress, enabled } = useHomeAddress();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

  const shouldShowMarker = enabled && address && formattedAddress;

  const handleMarkerClick = (): void => {
    setIsInfoWindowOpen(true);
  };

  const position = {
    lat: Number(address?.latitude),
    lng: Number(address?.longitude),
  };

  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
  };

  if (!shouldShowMarker) {
    return null;
  }

  return (
    <>
      {address && (
        <Marker
          title={t`Home`}
          position={position}
          color={colors.green['600']}
          onClick={handleMarkerClick}
          zIndex={SEARCH_PAGE_Z_INDEX.HOME_ADDRESS_MARKER.toNumber()}
        >
          {isInfoWindowOpen && (
            <InfoWindow position={position} onClose={() => setIsInfoWindowOpen(false)}>
              <HomeAddressCard
                address={formattedAddress}
                onEditClick={() => setIsEditModalOpen(true)}
              />
            </InfoWindow>
          )}
          <Image src={homeMarkerImage} description={t`Home`} width="100%" height="100%" />
        </Marker>
      )}
      <HomeEditModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} onEdit={onEdit} />
    </>
  );
};

export default HomeAddressMarker;
